import React, { useState, useEffect } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  FormGroup,
  FormHelperText,
  Grid,
  Divider,
  Paper,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  InputAdornment
} from '@mui/material';
import {
  Person,
  Mail,
  Phone,
  Group,
  Settings,
  Business,
  Source
} from '@mui/icons-material';
// Import your services
import { getContactTypes } from '../../service/Contacts/getContactTypes';
import { addContact } from '../../service/Contacts/addContact';
import { getEnquirySource } from '../../service/Contacts/getenquirySource';
import { getSalesperson } from '../../service/User/getSalesperson';



const validationSchema = Yup.object().shape({
  firstNameContact: Yup.string().required('First Name is required'),
  lastNameContact: Yup.string().required('Last Name is required'),
  contactEmail: Yup.string().email('Invalid email').nullable(),
  contactNumber: Yup.string().matches(/^\d{10}$/, 'Invalid phone number').nullable(),
  contactTypeID: Yup.string()
    .test('required', 'At least one Contact Type is required', value => value && value.split(',').filter(v => v.trim()).length > 0),
  salespersonid: Yup.string().required('Salesperson is required'),
  contactSourceId: Yup.string().required('Enquiry Source is required'),
});

const ContactAdd = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [contactTypes, setContactTypes] = useState([]);
  const [enquirySource, setEnquirySource] = useState([]);
  const [isSavingContact, setIsSavingContact] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [
          allUsersResponse,
          contactTypesResponse,
          enquirySourceResponse
        ] = await Promise.all([
          getSalesperson("contact_view"),
          getContactTypes(),
          getEnquirySource()
        ]);
        setUsers(allUsersResponse.data);
        setContactTypes(contactTypesResponse.data);
        setEnquirySource(enquirySourceResponse.data);
      } catch (error) {
        setError('Failed to load form data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSubmit = async (values) => {
    setIsSavingContact(true);
    try {
      const addcontactResponse = await addContact(values);
      
      if (addcontactResponse.status === 200 || addcontactResponse.status === 201) {
        // AlertUtils.showSuccess('Add Contact', 'Contact Added successfully');
        localStorage.setItem('editContact', 'true');
        navigate(`/contacts/${addcontactResponse.data.contactID}/edit`);
      }
    } catch (error) {
      setError('Failed to add contact. Please try again.');
    } finally {
      setIsSavingContact(false);
    }
  };

  const handleContactTypeChange = (type, values, setFieldValue) => {
    let currentSelection = values.contactTypeID 
      ? values.contactTypeID.split(',').filter(id => id.trim() !== '') 
      : [];

    if (type.ContactTypeID.toString() === '6') {
      if (currentSelection.includes('6')) {
        currentSelection = currentSelection.filter(id => id !== '6');
      } else {
        currentSelection = ['6'];
      }
    } else {
      if (currentSelection.includes('6')) {
        currentSelection = currentSelection.filter(id => id !== '6');
      }

      if (currentSelection.includes(type.ContactTypeID.toString())) {
        currentSelection = currentSelection.filter(id => id !== type.ContactTypeID.toString());
      } else {
        currentSelection.push(type.ContactTypeID.toString());
      }
    }

    setFieldValue('contactTypeID', currentSelection.join(','));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={2} sx={{ p: 4 }}>
        {/* Header */}
        <Box mb={4}>
          <Typography variant="h4" component="h1" gutterBottom>
            Add New Contact
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Fill in the details below to create a new contact
          </Typography>
        </Box>

        <Formik
          initialValues={{
            firstNameContact: '',
            lastNameContact: '',
            contactTypeID: '',
            contactSourceId: '',
            salespersonid: '',
            contactEmail: '',
            contactNumber: '',
            enableSMSBroadcast: 0,
            enabledForBroadCast: 0,
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ handleChange, handleBlur, handleSubmit, values, errors, touched, setFieldValue }) => (
            <form onSubmit={handleSubmit}>
              <Stack spacing={4}>
                {/* Contact Details Section */}
                <Card variant="outlined">
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={3}>
                      <Person sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6">Contact Details</Typography>
                    </Box>
                    
                    <Grid container spacing={3}>
                      <Grid >
                        <TextField
                          fullWidth
                          label="First Name"
                          name="firstNameContact"
                          value={values.firstNameContact}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.firstNameContact && Boolean(errors.firstNameContact)}
                          helperText={touched.firstNameContact && errors.firstNameContact}
                          required
                        />
                      </Grid>
                      
                      <Grid >
                        <TextField
                          fullWidth
                          label="Last Name"
                          name="lastNameContact"
                          value={values.lastNameContact}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.lastNameContact && Boolean(errors.lastNameContact)}
                          helperText={touched.lastNameContact && errors.lastNameContact}
                          required
                        />
                      </Grid>
                      
                      <Grid >
                        <TextField
                          fullWidth
                          label="Email"
                          name="contactEmail"
                          type="email"
                          value={values.contactEmail}
                          onChange={(e) => {
                            const text = e.target.value;
                            setFieldValue('contactEmail', text);
                            setFieldValue('enabledForBroadCast', text.trim() !== '' ? 1 : 0);
                          }}
                          onBlur={handleBlur}
                          error={touched.contactEmail && Boolean(errors.contactEmail)}
                          helperText={touched.contactEmail && errors.contactEmail}
                          slotProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Mail />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      
                      <Grid >
                        <TextField
                          fullWidth
                          label="Phone"
                          name="contactNumber"
                          value={values.contactNumber}
                          onChange={(e) => {
                            const text = e.target.value;
                            setFieldValue('contactNumber', text);
                            setFieldValue('enableSMSBroadcast', text.trim() !== '' ? 1 : 0);
                          }}
                          onBlur={handleBlur}
                          error={touched.contactNumber && Boolean(errors.contactNumber)}
                          helperText={touched.contactNumber && errors.contactNumber}
                          slotProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Phone />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Contact Type Section */}
                <Card variant="outlined">
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={3}>
                      <Group sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6">Contact Type</Typography>
                    </Box>
                    
                    <FormControl 
                      error={touched.contactTypeID && Boolean(errors.contactTypeID)}
                      component="fieldset"
                      fullWidth
                    >
                      <FormGroup>
                        <Grid container spacing={2}>
                          {contactTypes.map((type) => (
                            <Grid  md={4} key={type.ContactTypeID}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={values.contactTypeID.split(',').includes(type.ContactTypeID.toString())}
                                    onChange={() => handleContactTypeChange(type, values, setFieldValue)}
                                    disabled={values.contactTypeID.includes('6') && type.ContactTypeID.toString() !== '6'}
                                  />
                                }
                                label={type.Type}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </FormGroup>
                      {touched.contactTypeID && errors.contactTypeID && (
                        <FormHelperText>{errors.contactTypeID}</FormHelperText>
                      )}
                    </FormControl>
                  </CardContent>
                </Card>

                {/* Enquiry Details Section */}
                <Card variant="outlined">
                  <CardContent> 
                    <Box display="flex" alignItems="center" mb={3}>
                      <Business sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6">Enquiry Details</Typography>
                    </Box>
                      <Grid>
                        <FormControl 
                          fullWidth 
                          error={touched.contactSourceId && Boolean(errors.contactSourceId)}
                          required
                        >
                          <InputLabel>Enquiry Source</InputLabel>
                          <Select
                            value={values.contactSourceId}
                            label="Enquiry Source"
                            onChange={(e) => setFieldValue('contactSourceId', e.target.value)}
                          >
                            {enquirySource.map((source) => (
                              <MenuItem key={source.contactSourceId} value={source.contactSourceId}>
                                {source.source}
                              </MenuItem>
                            ))}
                          </Select>
                          {touched.contactSourceId && errors.contactSourceId && (
                            <FormHelperText>{errors.contactSourceId}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                      
                      <Grid >
                        <FormControl 
                          fullWidth 
                          error={touched.salespersonid && Boolean(errors.salespersonid)}
                          required
                        >
                          <InputLabel>Salesperson</InputLabel>
                          <Select
                            value={values.salespersonid}
                            label="Salesperson"
                            onChange={(e) => setFieldValue('salespersonid', e.target.value)}
                          >
                            {users.map((user) => (
                              <MenuItem key={user.userid} value={user.userid}>
                                {user.salespersonName}
                              </MenuItem>
                            ))}
                          </Select>
                          {touched.salespersonid && errors.salespersonid && (
                            <FormHelperText>{errors.salespersonid}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                  </CardContent>
                </Card>

                {/* Broadcast Options Section */}
                <Card variant="outlined">
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={3}>
                      <Settings sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6">Broadcast Options</Typography>
                    </Box>
                    
                    <FormGroup>
                      <Grid container spacing={2}>
                        <Grid >
                          <FormControlLabel
                            control={
                              <Switch
                                checked={values.enableSMSBroadcast === 1}
                                onChange={(e) => setFieldValue('enableSMSBroadcast', e.target.checked ? 1 : 0)}
                              />
                            }
                            label="SMS Broadcast"
                          />
                        </Grid>
                        
                        <Grid >
                          <FormControlLabel
                            control={
                              <Switch
                                checked={values.enabledForBroadCast === 1}
                                onChange={(e) => setFieldValue('enabledForBroadCast', e.target.checked ? 1 : 0)}
                              />
                            }
                            label="Email Broadcast"
                          />
                        </Grid>
                      </Grid>
                    </FormGroup>
                  </CardContent>
                </Card>

                {/* Form Actions */}
                <Box display="flex" justifyContent="flex-end" gap={2} pt={2}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/contacts')}
                    disabled={isSavingContact}
                    size="large"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSavingContact}
                    size="large"
                    startIcon={isSavingContact ? <CircularProgress size={20} /> : null}
                  >
                    {isSavingContact ? 'Saving...' : 'Save Contact'}
                  </Button>
                </Box>
              </Stack>
            </form>
          )}
        </Formik>
      </Paper>
    </Container>
  );
};

export default ContactAdd;